import { Controller } from "@hotwired/stimulus"

// NOTE: 案内状本文用のリッチエディタ。プレースホルダーをチップとして可視化しつつ、
//       太字 / 斜体 / 下線 / 取り消し線 / 文字色 / フォントサイズ / 行揃えの装飾を扱える。
//       DB には HTML 文字列 + `{{name}}` プレースホルダで保存し、 表示時に span チップに復元する。
const PLACEHOLDER_REGEX = /\{\{([^}]+)\}\}/g

// プレースホルダの DOM 表現に使うトークン ( 一時的に文字列保存→チップに復元するため )。
// 衝突しないユニークな文字列にしている。
const PLACEHOLDER_OPEN_TOKEN = "[[PH_OPEN_b8d72f]]"
const PLACEHOLDER_CLOSE_TOKEN = "[[PH_CLOSE_b8d72f]]"

// NOTE: contenteditable の base は text-sm ( 0.875rem ) なので「小」は明示的に小さく、
//       「中」以降が段階的に大きくなるように設定する。
const FONT_SIZE_MAP = {
  small: "0.75rem",
  medium: "1rem",
  large: "1.25rem",
  xlarge: "1.5rem"
}

export default class extends Controller {
  static targets = ["editor", "hidden", "colorInput", "fontSizeSelect", "surveySearch", "surveyButton"]
  static values = {
    autoResolved: { type: Array, default: [] },
    venue: { type: Array, default: [] },
    meeting: { type: Array, default: [] },
    survey: { type: Array, default: [] },
    // 旧名 → 正式名 の対応。 本文中の旧名は読み込み / 挿入時に正式名へリネームする。
    legacyAliases: { type: Object, default: { "インタビュー日時": "確定日時" } }
  }

  // 旧名を正式名にリネームする ( 未指定なら入力名そのまま )
  canonicalName(name) {
    return this.legacyAliasesValue[name] || name
  }

  connect() {
    this.render(this.hiddenTarget.value || "")
    // NOTE: execCommand のデフォルトは <font color="..."> 等の旧仕様タグを生成するため、
    //       <span style="..."> 形式に強制してサニタイズで残るようにする。
    try { document.execCommand("styleWithCSS", false, true) } catch (e) { /* ignore */ }
    this.serialize()
    // カーソル位置の装飾状態に応じてツールバーボタンをハイライト
    this.boundSelectionChange = this.updateToolbarState.bind(this)
    document.addEventListener("selectionchange", this.boundSelectionChange)
    // Backspace / Delete でプレースホルダーチップを削除できるよう keydown を listen
    this.boundKeydown = this.handleKeydown.bind(this)
    this.editorTarget.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    if (this.boundSelectionChange) {
      document.removeEventListener("selectionchange", this.boundSelectionChange)
    }
    if (this.boundKeydown) {
      this.editorTarget.removeEventListener("keydown", this.boundKeydown)
    }
  }

  // NOTE: contenteditable: false のプレースホルダーチップは Backspace / Delete で
  //       消えないことがあるため、隣接チップを検出して明示的に削除する。
  handleKeydown(event) {
    if (event.key !== "Backspace" && event.key !== "Delete") return
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (!range.collapsed) return

    const chip = event.key === "Backspace"
      ? this.findChipBefore(range)
      : this.findChipAfter(range)

    if (chip) {
      event.preventDefault()
      const parent = chip.parentNode
      const next = chip.nextSibling
      chip.remove()
      // カーソルを削除位置に置く
      const newRange = document.createRange()
      if (next) {
        newRange.setStartBefore(next)
      } else {
        newRange.selectNodeContents(parent)
        newRange.collapse(false)
      }
      sel.removeAllRanges()
      sel.addRange(newRange)
      this.savedRange = newRange.cloneRange()
      this.serialize()
    }
  }

  findChipBefore(range) {
    const node = range.startContainer
    const offset = range.startOffset
    if (node.nodeType === Node.TEXT_NODE) {
      if (offset > 0) return null
      return this.placeholderChip(node.previousSibling)
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      return this.placeholderChip(node.childNodes[offset - 1])
    }
    return null
  }

  findChipAfter(range) {
    const node = range.startContainer
    const offset = range.startOffset
    if (node.nodeType === Node.TEXT_NODE) {
      if (offset < node.length) return null
      return this.placeholderChip(node.nextSibling)
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      return this.placeholderChip(node.childNodes[offset])
    }
    return null
  }

  placeholderChip(node) {
    if (node && node.nodeType === Node.ELEMENT_NODE && node.dataset && node.dataset.placeholder) {
      return node
    }
    return null
  }

  // ボタンの mousedown で呼んでフォーカスをエディタに残しつつセレクションを保存する。
  // ( preventDefault しないとエディタからボタンへフォーカスが移り、 execCommand が動かない )
  preventBlur(event) {
    event.preventDefault()
    this.rememberSelection()
  }

  // 設問キーを検索ボックスで filter する。 label ( 小文字キー ) に部分一致するボタンだけ表示。
  filterSurvey() {
    if (!this.hasSurveySearchTarget) return
    const query = this.surveySearchTarget.value.trim().toLowerCase()
    this.surveyButtonTargets.forEach(btn => {
      const label = btn.dataset.label || ""
      btn.classList.toggle("hidden", query !== "" && !label.includes(query))
    })
  }

  // パレットのチップクリックで呼ばれる
  insert(event) {
    event.preventDefault()
    const name = event.currentTarget.dataset.placeholderName
    if (!name) return
    this.editorTarget.focus()
    this.restoreSelection()
    this.insertChipAtSelection(name)
    this.serialize()
  }

  // 入力イベント（input / compositionend）から呼ばれる
  serialize() {
    this.hiddenTarget.value = this.serializeEditor()
    this.hiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // ペースト時に書式を剥いでプレーンテキストとして挿入 ( 装飾は剥ぐ )
  paste(event) {
    event.preventDefault()
    const text = (event.clipboardData || window.clipboardData).getData("text")
    if (!text) return
    this.editorTarget.focus()
    document.execCommand("insertText", false, text)
    this.serialize()
  }

  // セレクションがエディタ外に出た時に最後の位置を覚えておく
  rememberSelection() {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (this.editorTarget.contains(range.commonAncestorContainer)) {
      this.savedRange = range.cloneRange()
    }
  }

  restoreSelection() {
    if (!this.savedRange) return
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(this.savedRange)
  }

  // -------- ツールバーアクション --------

  toggleBold(event) {
    this.exec(event, "bold")
  }

  toggleItalic(event) {
    this.exec(event, "italic")
  }

  toggleUnderline(event) {
    event.preventDefault()
    this.editorTarget.focus()
    this.restoreSelection()
    this.wrapSelectionWithTag("u")
    this.serialize()
  }

  toggleStrike(event) {
    event.preventDefault()
    this.editorTarget.focus()
    this.restoreSelection()
    this.wrapSelectionWithTag("s")
    this.serialize()
  }

  alignLeft(event) {
    this.exec(event, "justifyLeft")
  }

  alignCenter(event) {
    this.exec(event, "justifyCenter")
  }

  alignRight(event) {
    this.exec(event, "justifyRight")
  }

  changeColor(event) {
    const value = event.currentTarget.dataset.color || event.target.value
    this.exec(event, "foreColor", value)
  }

  changeFontSize(event) {
    const key = event.target.value
    const px = FONT_SIZE_MAP[key]
    if (!px) {
      // 「サイズ」プレースホルダ選択時は何もしない
      return
    }
    this.editorTarget.focus()
    this.restoreSelection()
    this.applyFontSize(px)
    this.serialize()
  }

  removeFormat(event) {
    this.exec(event, "removeFormat")
  }

  // 装飾コマンドの共通実行: フォーカスとセレクションを戻してから execCommand
  exec(event, command, value = null) {
    event.preventDefault()
    this.editorTarget.focus()
    this.restoreSelection()
    document.execCommand(command, false, value)
    this.serialize()
  }

  // -------- 内部メソッド --------

  // 選択範囲を指定タグ ( <u>, <s> など ) でラップする。 collapsed の場合は無視。
  // NOTE: execCommand("underline"/"strikeThrough") はブラウザ依存で <u>/<s> ではなく
  //       style だけのケースや保存されないケースがあるため、自前でタグを生成する。
  wrapSelectionWithTag(tagName) {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return
    if (range.collapsed) return

    const contents = range.extractContents()
    const wrapper = document.createElement(tagName)
    wrapper.appendChild(contents)
    range.insertNode(wrapper)

    const newRange = document.createRange()
    newRange.selectNodeContents(wrapper)
    sel.removeAllRanges()
    sel.addRange(newRange)
    this.savedRange = newRange.cloneRange()
  }

  // 選択範囲に font-size を span でラップする。 collapsed の場合は無視 ( ユーザーに選択を促す )。
  applyFontSize(px) {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return
    if (range.collapsed) return

    const contents = range.extractContents()
    const span = document.createElement("span")
    span.style.fontSize = px
    span.appendChild(contents)
    range.insertNode(span)

    const newRange = document.createRange()
    newRange.selectNodeContents(span)
    sel.removeAllRanges()
    sel.addRange(newRange)
    this.savedRange = newRange.cloneRange()
  }

  // selectionchange で呼ばれてツールバーボタンの active 状態を更新する。
  updateToolbarState() {
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    const states = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strike: document.queryCommandState("strikeThrough"),
      alignLeft: document.queryCommandState("justifyLeft"),
      alignCenter: document.queryCommandState("justifyCenter"),
      alignRight: document.queryCommandState("justifyRight")
    }

    this.element.querySelectorAll("[data-command]").forEach(btn => {
      const cmd = btn.dataset.command
      const active = states[cmd]
      btn.classList.toggle("bg-indigo-100", !!active)
      btn.classList.toggle("border-indigo-400", !!active)
      btn.classList.toggle("text-indigo-700", !!active)
    })

    // カラーパレット: 現在の foreColor と一致するボタンに枠を付ける
    const fore = this.normalizeColor(document.queryCommandValue("foreColor"))
    this.element.querySelectorAll("[data-color]").forEach(btn => {
      const hex = btn.dataset.color.toLowerCase()
      btn.classList.toggle("ring-2", hex === fore)
      btn.classList.toggle("ring-offset-1", hex === fore)
      btn.classList.toggle("ring-indigo-500", hex === fore)
    })

    // フォントサイズ select: カーソル位置の computed font-size から対応するキーを反映する
    if (this.hasFontSizeSelectTarget) {
      let node = sel.focusNode
      if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement
      if (node && this.editorTarget.contains(node)) {
        const computed = window.getComputedStyle(node).fontSize
        const key = this.matchFontSizeKey(computed)
        if (key && this.fontSizeSelectTarget.value !== key) {
          this.fontSizeSelectTarget.value = key
        }
      }
    }
  }

  // 計算済み font-size ( 例: "16px" ) を FONT_SIZE_MAP のキーに突き合わせる。
  matchFontSizeKey(computed) {
    if (!computed) return null
    const px = parseFloat(computed)
    if (Number.isNaN(px)) return null
    const rootPx = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16
    let best = null
    let bestDiff = Infinity
    for (const [key, val] of Object.entries(FONT_SIZE_MAP)) {
      const valPx = val.endsWith("rem") ? parseFloat(val) * rootPx : parseFloat(val)
      const diff = Math.abs(valPx - px)
      if (diff < bestDiff) {
        bestDiff = diff
        best = key
      }
    }
    // 0.5px 以内に最近接マッチがある場合のみ反映
    return bestDiff <= 0.5 ? best : null
  }

  // "rgb(220, 38, 38)" を "#dc2626" に正規化する。
  normalizeColor(value) {
    if (!value) return ""
    const v = value.toString().trim().toLowerCase()
    if (v.startsWith("#")) return v
    const m = v.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/)
    if (!m) return ""
    const hex = [m[1], m[2], m[3]].map(n => parseInt(n, 10).toString(16).padStart(2, "0")).join("")
    return `#${hex}`
  }

  // DB の文字列 ( HTML + {{name}} + 旧形式の \n 改行 ) をエディタへ復元する
  render(stored) {
    if (!stored) {
      this.editorTarget.innerHTML = ""
      return
    }
    // NOTE: 旧 plain text データは \n で改行されているので <br> に変換しないと一行にまとまる。
    const withBreaks = stored.replace(/\r?\n/g, "<br>")
    // {{name}} を一時トークンに置換してから HTML として挿入。 innerHTML 後にトークンをチップに置換する。
    const tokenized = withBreaks.replace(PLACEHOLDER_REGEX, (_, name) => {
      return `${PLACEHOLDER_OPEN_TOKEN}${name}${PLACEHOLDER_CLOSE_TOKEN}`
    })
    this.editorTarget.innerHTML = tokenized
    this.replaceTokensWithChips(this.editorTarget)
  }

  // 入力済みの一時トークンをチップに変換 ( テキストノード走査 )
  replaceTokensWithChips(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
    const targets = []
    while (walker.nextNode()) {
      const node = walker.currentNode
      if (node.nodeValue.includes(PLACEHOLDER_OPEN_TOKEN)) {
        targets.push(node)
      }
    }
    targets.forEach(node => this.splitTextNodeIntoChips(node))
  }

  splitTextNodeIntoChips(textNode) {
    const value = textNode.nodeValue
    const parent = textNode.parentNode
    if (!parent) return

    const parts = []
    let cursor = 0
    while (cursor < value.length) {
      const openIdx = value.indexOf(PLACEHOLDER_OPEN_TOKEN, cursor)
      if (openIdx === -1) {
        parts.push({ type: "text", value: value.slice(cursor) })
        break
      }
      if (openIdx > cursor) {
        parts.push({ type: "text", value: value.slice(cursor, openIdx) })
      }
      const closeIdx = value.indexOf(PLACEHOLDER_CLOSE_TOKEN, openIdx + PLACEHOLDER_OPEN_TOKEN.length)
      if (closeIdx === -1) {
        parts.push({ type: "text", value: value.slice(openIdx) })
        break
      }
      const name = value.slice(openIdx + PLACEHOLDER_OPEN_TOKEN.length, closeIdx)
      parts.push({ type: "chip", name })
      cursor = closeIdx + PLACEHOLDER_CLOSE_TOKEN.length
    }

    const frag = document.createDocumentFragment()
    parts.forEach(part => {
      if (part.type === "text") {
        if (part.value.length > 0) frag.appendChild(document.createTextNode(part.value))
      } else {
        frag.appendChild(this.makeChip(part.name))
      }
    })
    parent.replaceChild(frag, textNode)
  }

  makeChip(rawName) {
    const name = this.canonicalName(rawName)
    const span = document.createElement("span")
    span.contentEditable = "false"
    span.dataset.placeholder = name
    span.textContent = name
    const base = "inline-block rounded px-1.5 py-0.5 text-xs font-medium mx-0.5 align-baseline select-none cursor-default"
    // パレットのカテゴリと同じ配色で挿入する ( 会場 = amber、 ミーティング = emerald、 自動 = sky、 設問 = violet、 その他 = amber )
    let color
    if (this.meetingValue.includes(name)) {
      color = "bg-emerald-100 text-emerald-800"
    } else if (this.venueValue.includes(name)) {
      color = "bg-amber-100 text-amber-800"
    } else if (this.autoResolvedValue.includes(name)) {
      color = "bg-sky-100 text-sky-800"
    } else if (this.surveyValue.includes(name)) {
      color = "bg-violet-100 text-violet-800"
    } else {
      color = "bg-amber-100 text-amber-800"
    }
    span.className = `${base} ${color}`
    return span
  }

  insertChipAtSelection(name) {
    const sel = window.getSelection()
    let range
    if (sel && sel.rangeCount && this.editorTarget.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      range = sel.getRangeAt(0)
    } else {
      // セレクションがエディタ内にない場合は末尾に追加
      range = document.createRange()
      range.selectNodeContents(this.editorTarget)
      range.collapse(false)
    }
    range.deleteContents()
    const chip = this.makeChip(name)
    range.insertNode(chip)
    // チップの直後にカーソルを移動
    range.setStartAfter(chip)
    range.setEndAfter(chip)
    if (sel) {
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }

  // エディタ内容を HTML + {{name}} の文字列にシリアライズする。
  // プレースホルダチップ ( span data-placeholder ) は {{name}} に置換した上で
  // 他の HTML 要素はそのまま残す。
  serializeEditor() {
    const clone = this.editorTarget.cloneNode(true)
    // チップを {{name}} のテキストノードに置換
    clone.querySelectorAll("[data-placeholder]").forEach(chip => {
      const name = chip.dataset.placeholder
      const replacement = document.createTextNode(`{{${name}}}`)
      chip.parentNode.replaceChild(replacement, chip)
    })
    return clone.innerHTML
  }
}
